import { IDialogProps } from 'Components/Core/Dialog/types';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { isGreaterThanOrEqualTo, toIQuantity } from 'Data/ViewModels/Core/IQuantity';
import { valueOrDefault } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { ascend, sort } from 'ramda';
import React, { useEffect, useState } from 'react';
import { MdAddCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions as pltActions } from '../../../../Data/ReduxViewModels/PlateLoading';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { Button, Dialog, Field, Message, NumericInput, TextOutput } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { Grid, GridFieldSection, ListFieldSection } from '../../../Layout';
import { LoadIncrement } from '../types';

const minimumPressureForAutoCreate = toIQuantity(25, 'kN/m²');

let ManageLoadIncrementsDialog: React.FC<IWorksheetComponent & IDialogProps> = ({
  onClose,
  visible,
  namePrefix,
  createWorksheetSelector,
  ...otherProps
}) => {
  const dispatch = useDispatch();

  const [closeWhenSubmitted, setCloseWhenSubmitted] = useState(false);
  const [existingIncrementDeleted, setExistingIncrementDeleted] = useState(false);
  const loadIncrements: LoadIncrement[] = useSelector(
    pipe(
      (state) => state.plateLoading.loadIncrementsManagement.loadIncrements,
      valueOrDefault([]),
      (records: LoadIncrement[]) => {
        return sort(
          ascend((r: LoadIncrement) => Number(r.IncrementIndex)),
          records
        );
      }
    ),
    deepEquals
  );

  const applyEquipmentLoadToReadings = useSelector(
    pipe(createWorksheetSelector!('Preparation.ApplyEquipmentLoadToReadings'), valueOrDefault(false))
  );
  const existingLoadIncrements: Guid[] = useSelector(
    pipe(createWorksheetSelector!('LoadIncrements'), valueOrDefault([]), (records: LoadIncrement[]) => {
      return records.map((r) => r.LoadIncrementId);
    }),
    deepEquals
  );
  const autoCreateEnabled = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.MaxRequiredPressure'),
      (p) => p && isGreaterThanOrEqualTo(p, minimumPressureForAutoCreate) && loadIncrements.length === 0
    )
  );
  const creating = useSelector((s) => s.plateLoading.loadIncrementsManagement.status.creating);
  const saving = useSelector((s) => s.worksheetEditor.status.saving);

  useEffect(() => {
    dispatch(pltActions.initTempLoadIncrements());
    setExistingIncrementDeleted(false);
  }, [dispatch, visible]);

  useEffect(() => {
    if (!saving && closeWhenSubmitted) {
      setCloseWhenSubmitted(false);
      onClose!();
    }
  }, [saving, closeWhenSubmitted, onClose]);

  const removeRow = (index: number, incrementId: Guid) => {
    if (existingLoadIncrements.some((l) => l === incrementId)) setExistingIncrementDeleted(true);
    dispatch(pltActions.removeTempLoadIncrement({ index }));
  };

  type RowDefinition = {
    data?: any;
    onRemove?: () => void;
  };
  var gridRows: RowDefinition[] = [
    ...(loadIncrements?.map((s: LoadIncrement, i: number) => ({
      data: [
        <Field input={<TextOutput />} value={s.IncrementIndex + 1} />,
        <Field
          input={<NumericInput />}
          value={s.DesiredPressure?.value}
          name="LoadIncrements.DesiredPressure"
          onChange={(e: any) => {
            dispatch(
              pltActions.setTempLoadIncrementValue({
                index: i,
                name: 'DesiredPressure',
                value: toIQuantity(e.data.value, 'kN/m²'),
              })
            );
          }}
        />,
        <Field input={<TextOutput />} name="LoadIncrements.DesiredLoad" value={s.DesiredLoad?.value} />,
        <Button
          subtle
          success
          onClick={() => dispatch(pltActions.addTempLoadIncrement({ index: s.IncrementIndex + 1 }))}
        >
          <MdAddCircle size="2em" />
        </Button>,
      ],
      onRemove: () => removeRow(i, s.LoadIncrementId),
    })) ?? []),
  ];

  var manageLoadIncrementsDialogContent = (
    <>
      <ListFieldSection single wide>
        <Field input={<TextOutput />} name="Preparation.MaxRequiredPressure" />
        <Field input={<TextOutput />} name="Equipment.Plate1.Area" label="Plate Area" />
        <Field input={<TextOutput />} name="Preparation.CombinedEquipmentLoad" visible={applyEquipmentLoadToReadings} />
        <div>
          <Button
            disabled={!autoCreateEnabled || creating}
            onClick={() => dispatch(pltActions.autoCreateLoadIncrements())}
          >
            Auto-Create
          </Button>
        </div>
      </ListFieldSection>
      <GridFieldSection>
        <Grid
          colHeaders={['Increment', 'Desired Pressure (kN/m²)', 'Desired Load (kN)', '']}
          colWidths={[90, 120, 100, 44]}
          data={gridRows.map((r) => r.data)}
          onRemoveRow={(i) => gridRows[i].onRemove?.()}
          onCreateRow={() => dispatch(pltActions.addTempLoadIncrement({ index: loadIncrements.length }))}
        />
      </GridFieldSection>
      {existingIncrementDeleted && (
        <Message error>
          Data in removed load increments will be lost after clicking done. This data will not be able to be recovered.
        </Message>
      )}
    </>
  );

  return (
    <Dialog
      {...otherProps}
      title="Manage Load Increments"
      width="445px"
      visible={visible}
      onClose={onClose}
      buttons={[
        <Button
          primary
          key="1"
          onClick={() => {
            setCloseWhenSubmitted(true);
            dispatch(actions.setValue({ name: `${namePrefix}.LoadIncrements`, value: loadIncrements, sync: true }));
          }}
          disabled={creating}
          loading={saving && closeWhenSubmitted}
        >
          Done
        </Button>,
        <Button key="0" disabled={creating} onClick={onClose as any}>
          Cancel
        </Button>,
      ]}
    >
      {visible && manageLoadIncrementsDialogContent}
    </Dialog>
  );
};

ManageLoadIncrementsDialog = withInjectedProps(ManageLoadIncrementsDialog);

export { ManageLoadIncrementsDialog };
